import { useState } from "react";
import type { Budget, Category, GroupPreferences, Vibe } from "../api/types";
import { VENUE_ATTRIBUTES } from "../utils/attributes";

// ── Subcategory definitions per category ──
// Each has a display label, and optional vibe/budget suggestions that
// auto-populate when selected.
interface Subcategory {
  label: string;
  suggestVibe?: Vibe;
  suggestBudget?: Budget;
}

const SUBCATEGORIES: Record<Category, Subcategory[]> = {
  food: [
    { label: "Restaurant" },
    { label: "Fine Dining", suggestVibe: "date", suggestBudget: "high" },
    { label: "Cafe", suggestVibe: "casual" },
    { label: "Coffee", suggestVibe: "quiet" },
    { label: "Bakery", suggestVibe: "casual" },
    { label: "Brunch", suggestVibe: "casual" },
    { label: "Breakfast" },
    { label: "Desserts" },
    { label: "Ice Cream", suggestVibe: "casual" },
    { label: "Fast Food", suggestBudget: "low" },
    { label: "Diner", suggestVibe: "casual", suggestBudget: "low" },
  ],
  drinks: [
    { label: "Bar" },
    { label: "Pub", suggestVibe: "lively" },
    { label: "Cocktail Bar", suggestVibe: "date", suggestBudget: "high" },
    { label: "Brewery", suggestVibe: "lively" },
    { label: "Brewpub", suggestVibe: "lively" },
    { label: "Wine Bar", suggestVibe: "date", suggestBudget: "high" },
    { label: "Beer Garden", suggestVibe: "casual" },
    { label: "Lounge", suggestVibe: "date", suggestBudget: "high" },
    { label: "Sports Bar", suggestVibe: "lively" },
    { label: "Nightclub", suggestVibe: "lively" },
    { label: "Hookah Lounge", suggestVibe: "casual" },
  ],
  activity: [
    { label: "Bowling", suggestVibe: "lively" },
    { label: "Entertainment", suggestVibe: "lively" },
    { label: "Arcade", suggestVibe: "lively" },
    { label: "Karaoke", suggestVibe: "lively" },
    { label: "Comedy", suggestVibe: "lively" },
    { label: "Live Music", suggestVibe: "lively" },
    { label: "Go-Karting", suggestVibe: "lively" },
    { label: "Mini Golf", suggestVibe: "casual" },
    { label: "Adventure Sports", suggestVibe: "lively" },
    { label: "Aquarium", suggestVibe: "casual" },
    { label: "Theme Park", suggestVibe: "lively" },
    { label: "Water Park", suggestVibe: "lively" },
  ],
  sport: [
    { label: "Gym" },
    { label: "Fitness" },
    { label: "Pool" },
    { label: "Tennis" },
    { label: "Ice Skating", suggestVibe: "lively" },
    { label: "Golf", suggestBudget: "high" },
    { label: "Sports Complex" },
    { label: "Stadium", suggestVibe: "lively" },
  ],
  culture: [
    { label: "Museum", suggestVibe: "quiet" },
    { label: "Art Museum", suggestVibe: "quiet" },
    { label: "Gallery", suggestVibe: "quiet" },
    { label: "Art Studio" },
    { label: "Cinema", suggestVibe: "casual" },
    { label: "Theatre", suggestVibe: "date" },
    { label: "Concert Hall", suggestVibe: "date", suggestBudget: "high" },
    { label: "Landmark" },
    { label: "Planetarium", suggestVibe: "quiet" },
    { label: "Library", suggestVibe: "quiet" },
  ],
  outdoors: [
    { label: "Park", suggestVibe: "casual" },
    { label: "Garden", suggestVibe: "quiet" },
    { label: "Botanical Garden", suggestVibe: "quiet" },
    { label: "Beach", suggestVibe: "casual" },
    { label: "Hiking", suggestVibe: "casual" },
    { label: "Zoo", suggestVibe: "casual" },
    { label: "Scenic Spot", suggestVibe: "quiet" },
    { label: "Picnic Area", suggestVibe: "casual" },
  ],
  work: [
    { label: "Coworking Space", suggestVibe: "quiet" },
    { label: "Business Center", suggestVibe: "quiet" },
    { label: "Meeting Room", suggestVibe: "quiet" },
    { label: "Cafe (Work)", suggestVibe: "quiet" },
    { label: "Hotel Lobby", suggestVibe: "quiet" },
    { label: "Library (Work)", suggestVibe: "quiet" },
  ],
};

const CATEGORIES: { value: Category; label: string; desc: string }[] = [
  { value: "food", label: "Food", desc: "Restaurants, cafes, bakeries, desserts" },
  { value: "drinks", label: "Drinks", desc: "Bars, pubs, breweries, cocktail bars" },
  { value: "activity", label: "Activities", desc: "Bowling, arcades, karaoke, comedy, go-karting" },
  { value: "sport", label: "Sport & Fitness", desc: "Gyms, pools, courts, skating" },
  { value: "culture", label: "Culture", desc: "Museums, theatres, galleries, landmarks" },
  { value: "outdoors", label: "Outdoors", desc: "Parks, gardens, beaches, hiking" },
  { value: "work", label: "Work & Meet", desc: "Coworking, meeting rooms, business centres" },
];

const VIBES: { value: Vibe; label: string }[] = [
  { value: "casual", label: "Casual & relaxed" },
  { value: "quiet", label: "Quiet & calm" },
  { value: "lively", label: "Lively & energetic" },
  { value: "date", label: "Date night" },
];

const BUDGETS: { value: Budget; label: string }[] = [
  { value: "low", label: "Budget-friendly" },
  { value: "mid", label: "Mid-range" },
  { value: "high", label: "Premium" },
];

interface Props {
  prefs: GroupPreferences;
  onChange: (prefs: GroupPreferences) => void;
  disabled: boolean;
}

export function PreferencesStep({ prefs, onChange, disabled }: Props) {
  const slot = prefs.time_window.mode === "slot";
  const [showMore, setShowMore] = useState(false);

  function toggleCategory(value: Category) {
    const next = prefs.categories.includes(value)
      ? prefs.categories.filter((c) => c !== value)
      : [...prefs.categories, value];
    // When deselecting a category, remove its subcategories too
    const removedSubs = !next.includes(value)
      ? (SUBCATEGORIES[value] || []).map((s) => s.label)
      : [];
    const nextSubs = removedSubs.length > 0
      ? prefs.subcategories.filter((s) => !removedSubs.includes(s))
      : prefs.subcategories;
    onChange({ ...prefs, categories: next, subcategories: nextSubs });
  }

  function toggleSubcategory(sub: Subcategory) {
    const has = prefs.subcategories.includes(sub.label);
    const nextSubs = has
      ? prefs.subcategories.filter((s) => s !== sub.label)
      : [...prefs.subcategories, sub.label];

    const updates: Partial<GroupPreferences> = { subcategories: nextSubs };

    // Auto-suggest vibe/budget when adding a subcategory (only if user
    // hasn't already set one manually)
    if (!has) {
      if (sub.suggestVibe && prefs.vibe == null) {
        updates.vibe = sub.suggestVibe;
      }
      if (sub.suggestBudget && prefs.budget == null) {
        updates.budget = sub.suggestBudget;
      }
    }

    onChange({ ...prefs, ...updates });
  }

  function toggleAttr(key: string) {
    const next = prefs.required_attrs.includes(key)
      ? prefs.required_attrs.filter((a) => a !== key)
      : [...prefs.required_attrs, key];
    onChange({ ...prefs, required_attrs: next });
  }

  const attrGroups = VENUE_ATTRIBUTES.reduce<Record<string, typeof VENUE_ATTRIBUTES>>(
    (acc, attr) => {
      (acc[attr.group] ??= []).push(attr);
      return acc;
    },
    {},
  );

  return (
    <section className="card">
      <h2>What are you looking for?</h2>
      <p className="muted small">All optional. Skip everything for a mixed slate of the best options.</p>

      {/* ── Time ── */}
      <div className="pref-section">
        <span className="label">When</span>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={slot}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                ...prefs,
                time_window: e.target.checked
                  ? { mode: "slot", start: "18:00", end: "20:00" }
                  : { mode: "whole_day" },
              })
            }
          />
          <span>Specific time window</span>
        </label>
        {slot && (
          <div className="time-pickers">
            <input
              type="time"
              value={prefs.time_window.start ?? "18:00"}
              disabled={disabled}
              aria-label="From"
              onChange={(e) =>
                onChange({ ...prefs, time_window: { ...prefs.time_window, start: e.target.value } })
              }
            />
            <span className="muted">to</span>
            <input
              type="time"
              value={prefs.time_window.end ?? "20:00"}
              disabled={disabled}
              aria-label="To"
              onChange={(e) =>
                onChange({ ...prefs, time_window: { ...prefs.time_window, end: e.target.value } })
              }
            />
          </div>
        )}
      </div>

      {/* ── Categories + expandable subcategories ── */}
      <div className="pref-section">
        <span className="label">What kind of place</span>
        <div className="category-list">
          {CATEGORIES.map((cat) => {
            const active = prefs.categories.includes(cat.value);
            const subs = SUBCATEGORIES[cat.value] || [];
            return (
              <div key={cat.value} className="category-block">
                <button
                  type="button"
                  disabled={disabled}
                  className={`category-card${active ? " on" : ""}`}
                  onClick={() => toggleCategory(cat.value)}
                >
                  <span className="category-label">{cat.label}</span>
                  <span className="category-desc">{cat.desc}</span>
                </button>
                {active && subs.length > 0 && (
                  <div className="subcategory-chips">
                    {subs.map((sub) => {
                      const subActive = prefs.subcategories.includes(sub.label);
                      return (
                        <button
                          key={sub.label}
                          type="button"
                          disabled={disabled}
                          className={`chip sub-chip${subActive ? " on" : ""}`}
                          onClick={() => toggleSubcategory(sub)}
                        >
                          {sub.label}
                          {sub.suggestVibe && !subActive && (
                            <span className="sub-hint">{sub.suggestVibe}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Vibe & Budget ── */}
      <div className="pref-row">
        <div className="pref-section">
          <span className="label">Vibe</span>
          <div className="chips">
            <button
              type="button"
              disabled={disabled}
              className={prefs.vibe == null ? "chip on" : "chip"}
              onClick={() => onChange({ ...prefs, vibe: null })}
            >
              Any
            </button>
            {VIBES.map((v) => (
              <button
                key={v.value}
                type="button"
                disabled={disabled}
                className={prefs.vibe === v.value ? "chip on" : "chip"}
                onClick={() => onChange({ ...prefs, vibe: v.value })}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pref-section">
          <span className="label">Budget</span>
          <div className="chips">
            <button
              type="button"
              disabled={disabled}
              className={prefs.budget == null ? "chip on" : "chip"}
              onClick={() => onChange({ ...prefs, budget: null })}
            >
              Any
            </button>
            {BUDGETS.map((b) => (
              <button
                key={b.value}
                type="button"
                disabled={disabled}
                className={prefs.budget === b.value ? "chip on" : "chip"}
                onClick={() => onChange({ ...prefs, budget: b.value })}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── More options (collapsed by default) ── */}
      <div className="pref-section">
        <button
          type="button"
          className="link toggle-more"
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? "Less options" : "More options"} {showMore ? "\u25B4" : "\u25BE"}
        </button>
      </div>

      {showMore && (
      <div className="pref-section">
        <span className="label">Preferences</span>
        <p className="muted small" style={{ margin: "0 0 .3rem" }}>
          Places with these get ranked higher.
        </p>
        {Object.entries(attrGroups).map(([group, attrs]) => (
          <div key={group} className="attr-group">
            <span className="attr-group-label">{group}</span>
            <div className="chips">
              {attrs.map((attr) => (
                <button
                  key={attr.key}
                  type="button"
                  disabled={disabled}
                  className={prefs.required_attrs.includes(attr.key) ? "chip on" : "chip"}
                  onClick={() => toggleAttr(attr.key)}
                >
                  {attr.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}
    </section>
  );
}
